import React, { useState, useRef, useEffect } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { IComment, IGame } from 'utils/Interfaces';
import { createPath, ROUTE } from 'utils/Routing';
import fetchData from 'utils/FetchData';
import useLocalStorage from 'hooks/useLocalStorage';
import useFetch from 'hooks/useFetch';
import Loading from 'components/Loading/Loading';
import Modal from 'components/Modal/Modal';
import Comment from 'components/Comment/Comment';
import ImagePlaceHolder from 'assets/placeholder.png';
import './GamePage.css';

const GamePage = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [gameData, setGameData] = useState<IGame>();
  const [comments, setComments] = useState<IComment[]>();
  const [value] = useLocalStorage('AUTH');

  const { gameID }: { gameID: string } = useParams();
  const filteredGameID: number = Number.isNaN(Number.parseInt(gameID, 10)) ? 0 : Number.parseInt(gameID, 10);

  const { response: gameDataResponse, error: gameDataError, isLoading: gameDataIsLoading } = useFetch({
    method: 'get',
    url: `/games/${filteredGameID}`,
  });

  const { response: commentsResponse, isLoading: commentsIsLoading } = useFetch({
    method: 'get',
    url: `/games/${filteredGameID}/comments`,
  });

  useEffect(() => {
    setGameData(gameDataResponse);
    setComments(commentsResponse);
  }, [gameDataResponse, commentsResponse]);

  const commentInput = useRef<HTMLTextAreaElement>(null!);
  const commentError = useRef<HTMLParagraphElement>(null!);

  if (gameDataIsLoading || commentsIsLoading) {
    return <Loading />;
  }

  if (gameDataError.error || gameDataResponse.length === 0) {
    const errorHandler = () => {
      setIsOpen(false);
      return <Redirect to={createPath({ path: ROUTE.HOME })} push />;
    };
    return (
      <Modal
        open={isOpen}
        info={gameDataError.body || `Failed to fetch a game with that ID`}
        type='error'
        onClose={errorHandler}
      />
    );
  }

  const handleAddComment = async () => {
    if (!commentInput.current.value) {
      commentError.current.innerText = 'Please enter a comment.';
      commentInput.current.classList.add('error');
      return false;
    }
    commentInput.current.classList.remove('error');
    commentInput.current.classList.add('success');
    commentError.current.innerText = '';

    const { response, hasError } = await fetchData({
      method: 'post',
      url: `/games/${filteredGameID}/comment`,
      data: JSON.stringify({ body: commentInput.current.value }),
      config: { headers: { Authorization: `Bearer ${value.jwt}` } },
    });

    if (!hasError.error && response) {
      commentInput.current.value = '';
      const { response }: { response: IComment[] } = await fetchData({
        method: 'get',
        url: `/games/${filteredGameID}/comments`,
      });
      setComments(response);
      return true;
    }

    return (
      <Modal
        open={isOpen}
        info={hasError.body || `Failed to send the comment.`}
        type='error'
        onClose={() => setIsOpen(false)}
      />
    );
  };

  const commentsFiltered =
    comments?.map((comment: IComment) => {
      return {
        id: comment.id,
        username: comment.user.username,
        comment: comment.body,
      };
    }) ?? [];

  const publishers = gameData?.publishers?.map((publisher: any) => publisher.name).join(', ');

  const platforms = gameData?.platforms?.map((platform: any) => platform.name).join(', ');

  return (
    <>
      <div className='game-information__wrapper'>
        <div className='game-information__container'>
          <div className='game-information__header'>
            <div
              className='game-information__background'
              style={{ backgroundImage: `url(${gameData?.cover_art ? gameData.cover_art.url : ''})` }}
            />
            <img src={gameData?.cover_art?.url || ImagePlaceHolder} alt='' className='game-information__image' />
            <div className='gradient' />
            <div className='game-information__header-info'>
              <p className='game__author'>{publishers}</p>
              <p className='game-information__title'>{gameData?.name}</p>
            </div>
          </div>

          <div className='game-information__content-wrapper'>
            <div className='game-information__info'>
              <p className='game-information__field'>
                Platform(s): <span className='game-information__data'>{platforms}</span>
              </p>
              <p className='game-information__field'>
                Genre: <span className='game-information__data'>{gameData?.genre?.name}</span>
              </p>
              <p className='game-information__field'>
                Released on <span className='game-information__data'>{gameData?.release_year}</span>
              </p>
              <p className='game-information__field'>
                Price: <span className='game-information__data'>{`$${gameData?.price}`}</span>
              </p>
            </div>
          </div>
        </div>

        <div className='add-comment__container'>
          <p className='add-comment__title'>Add a comment</p>

          <span className='add-comment__name'>{value.user.username}</span>

          <div className='add-comment__comment'>
            <label htmlFor='comment' className='add-comment__label'>
              Comment
              <textarea ref={commentInput} id='comment' name='comment' className='input' maxLength={1024} />
            </label>
            <p ref={commentError} id='p-comment' className='feedback error' />
          </div>

          <button onClick={handleAddComment} type='button' className='action__button' style={{ gridColumn: '2 span' }}>
            <span>Add comment</span>
          </button>
        </div>

        <div className='comments__container'>
          <p className='comments_title'>Comments</p>
          {commentsFiltered.length ? (
            commentsFiltered.map(({ username, comment, id }: any) => {
              return <Comment author={username} comment={comment} key={id} />;
            })
          ) : (
            <div className='content__no-content'>
              <p>There are no comments. Write the first!</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(GamePage);
